import { z } from 'zod';
import { closeFollowUpSchema } from '../follow-up-entitlement';
import { ORDER_FULFILMENT, ORDER_LINE_PRIORITY, orderLineQuantitySchema } from '../order-lines';
export const COMPLAINT_DURATION_UNIT = ['hours', 'days', 'weeks', 'months', 'years'];
export const DISPOSITION = [
    'home',
    'follow_up',
    'referred_internal',
    'referred_external',
    'discharged_to_gp',
    'admitted',
    'sent_to_er',
    'left_against_advice',
    'deceased',
];
export const DISPOSITION_LABEL = {
    home: 'Home',
    follow_up: 'Home, with a follow-up',
    referred_internal: 'Referred to a colleague here',
    referred_external: 'Referred elsewhere',
    discharged_to_gp: 'Back to the family doctor',
    admitted: 'Admitted',
    sent_to_er: 'Sent to emergency',
    left_against_advice: 'Left against advice',
    deceased: 'Died',
};
export const CLAIM_FLAG = ['chronic', 'congenital', 'rta', 'work_related', 'vaccination', 'check_up', 'psychiatric', 'infertility', 'pregnancy'];
export const CLAIM_FLAG_LABEL = {
    chronic: 'Chronic condition',
    congenital: 'Congenital',
    rta: 'Road traffic accident',
    work_related: 'Work-related',
    vaccination: 'Vaccination',
    check_up: 'Check-up',
    psychiatric: 'Psychiatric',
    infertility: 'Infertility',
    pregnancy: 'Pregnancy',
};
export const CONDITION_ONSET = ['present_on_arrival', 'new_onset', 'not_reported'];
export const ALLERGY_STATUS = ['unknown', 'nka', 'has_allergies'];
export const ROS_SYSTEMS = [
    'constitutional',
    'eyes',
    'ent',
    'cardiovascular',
    'respiratory',
    'gastrointestinal',
    'genitourinary',
    'musculoskeletal',
    'skin',
    'neurological',
    'psychiatric',
    'endocrine',
];
export const ROS_SYSTEM_LABEL = {
    constitutional: 'General',
    eyes: 'Eyes',
    ent: 'Ear, nose and throat',
    cardiovascular: 'Heart and circulation',
    respiratory: 'Breathing',
    gastrointestinal: 'Stomach and bowel',
    genitourinary: 'Urinary and genital',
    musculoskeletal: 'Muscles and joints',
    skin: 'Skin',
    neurological: 'Nerves',
    psychiatric: 'Mood and mind',
    endocrine: 'Glands and hormones',
};
export const ROS_FINDING = ['normal', 'abnormal', 'not_examined'];
export const rosFindingsSchema = z
    .record(z.string(), z.enum(ROS_FINDING))
    .refine((value) => Object.keys(value).every((key) => ROS_SYSTEMS.includes(key)), 'Unknown body system');
export function allNormalExcept(abnormal) {
    return Object.fromEntries(ROS_SYSTEMS.map((system) => [system, abnormal.includes(system) ? 'abnormal' : 'normal']));
}
export const PROBLEM_STATUS = ['active', 'resolved'];
export const addProblemSchema = z.object({
    icdCode: z.string().trim().min(1).max(20),
    icdTitle: z.string().trim().min(1).max(400),
    onsetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    encounterId: z.string().uuid().optional(),
});
export const updateProblemSchema = z.object({
    status: z.enum(PROBLEM_STATUS),
});
export const stillTakingSchema = z.object({
    stillTaking: z.boolean(),
});
export const removeAllergySchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
export const removeProblemSchema = z
    .object({
    reason: z.string().trim().min(3).max(300).optional(),
})
    .default({});
export const closeEncounterSchema = z.object({
    disposition: z.enum(DISPOSITION).optional(),
    feeOverrideReason: z.string().trim().min(5).max(300).optional(),
    followUp: closeFollowUpSchema.optional(),
});
export function suggestedDisposition(followUpDate) {
    return followUpDate ? 'follow_up' : 'home';
}
export function nkaRefusal(activeAllergies) {
    return activeAllergies > 0 ? 'The patient has recorded allergies; remove them before marking no known allergies' : null;
}
export function principalDiagnosisRequired(setting, modules) {
    return setting || modules.has('nphies');
}
export const ORDER_URGENCY = ['routine', 'urgent', 'emergency'];
export const referralOrderSchema = z
    .object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid(),
    kind: z.enum(['internal', 'external']),
    targetDoctorId: z.string().uuid().optional(),
    targetDepartmentId: z.string().uuid().optional(),
    externalTarget: z.string().trim().min(2).max(240).optional(),
    specialty: z.string().trim().max(120).optional(),
    urgency: z.enum(ORDER_URGENCY).default('routine'),
    reason: z.string().trim().min(3).max(1000),
    sendNow: z.boolean().default(false),
})
    .superRefine((value, context) => {
    if (value.kind === 'internal' && !value.targetDoctorId && !value.targetDepartmentId) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['targetDoctorId'], message: 'Choose the colleague or the department' });
    }
    if (value.kind === 'external' && !value.externalTarget) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['externalTarget'], message: 'Say where the patient is referred' });
    }
    if (value.sendNow && (value.kind !== 'internal' || !value.targetDoctorId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['sendNow'], message: 'Only a referral to a named colleague here can be sent now' });
    }
});
export const admissionRequestSchema = z.object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid(),
    wardClass: z.string().trim().max(40).optional(),
    urgency: z.enum(ORDER_URGENCY).default('routine'),
    reason: z.string().trim().min(3).max(1000),
    estimatedStayDays: z.number().int().min(1).max(365).optional(),
});
export const procedureOrderLineSchema = z.object({
    chargeItemId: z.string().uuid(),
    quantity: orderLineQuantitySchema.default(1),
    fulfilment: z.enum(ORDER_FULFILMENT).optional(),
    notes: z.string().trim().max(600).optional(),
});
export const procedureOrderSchema = z
    .object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid(),
    chargeItemId: z.string().uuid().optional(),
    notes: z.string().trim().max(600).optional(),
    priority: z.enum(ORDER_LINE_PRIORITY).optional(),
    clinicalNotes: z.string().trim().max(2000).optional(),
    items: z.array(procedureOrderLineSchema).min(1).max(20).optional(),
})
    .superRefine((value, context) => {
    if (!value.chargeItemId && !value.items?.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'Choose at least one procedure' });
    }
    const services = (value.items ?? []).map((item) => item.chargeItemId);
    if (new Set(services).size !== services.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'The same service is listed twice; raise its quantity instead' });
    }
});
export const completeProcedureSchema = z.object({
    note: z.string().trim().min(3).max(4000),
});
export const referralsQuerySchema = z.object({
    scope: z.enum(['mine', 'all']).default('mine'),
    cursor: z.string().uuid().optional(),
});
export const admissionRequestsQuerySchema = z.object({
    branchId: z.string().uuid().optional(),
    cursor: z.string().uuid().optional(),
});
//# sourceMappingURL=emr-core.js.map